import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/cn";

const inputVariants = cva(
  "flex w-full touch-manipulation transition-colors file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-xs focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "h-11 rounded-xl bg-input p-3.5 placeholder:text-muted-foreground placeholder:text-sm focus-visible:ring-2 focus-visible:ring-ring sm:text-xs",
        form: "h-10 rounded-lg border border-twi-gray-950 bg-twi-gray-950 px-3 py-2 font-medium text-base text-white leading-6 placeholder:font-medium placeholder:text-[#5C5C5C] placeholder:text-base placeholder:leading-6 focus:border-twi-gray-500 focus-visible:ring-1 focus-visible:ring-twi-gray-950 sm:text-base sm:placeholder:text-base",
        phone:
          "h-full rounded-none border-0 bg-transparent px-0 py-0 font-medium text-base text-white leading-6 placeholder:font-medium placeholder:text-base placeholder:text-twi-gray-600 placeholder:leading-6 focus-visible:ring-0 sm:text-base sm:placeholder:text-base",
      },
      error: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "form",
        error: true,
        className: "border-twi-red-400 focus-visible:border-twi-red-400",
      },
    ],
    defaultVariants: {
      variant: "default",
      error: false,
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  "data-testid"?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, variant, error, "data-testid": dataTestId, ...props },
    ref,
  ) => {
    return (
      <input
        data-testid={dataTestId ?? "ui-input"}
        type={type}
        className={cn(inputVariants({ variant, error }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input, inputVariants };
